import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { changelogService, type CohortMember } from '@/services/changelog';

/** The cell of the cohort chart a click opened. Null closes the sheet. */
export interface CohortSelection {
  cohort: 'before' | 'since';
  fromDay: number;
  toDay: number | null;
  /** Bucket and cohort labels the chart already computed, reused verbatim. */
  bucketLabel: string;
  cohortLabel: string;
  /** The bar's own numbers, so the header reads before the fetch lands. */
  converted: number;
  eligible: number;
  value: number | null;
  /** Total MRR behind a money bar; undefined on a rate bar. */
  amount?: number;
  unit: 'percent' | 'count' | 'currency';
}

interface Props {
  slug: string;
  /** Decides which population the API lists, so it must be the clicked bar's. */
  metricKey: string;
  selection: CohortSelection | null;
  onClose: () => void;
}

const dateTime = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

/**
 * The gap as two units at most, "9d 2h" rather than "9.1d": a tenth of a day is
 * not a unit anyone thinks in. Most conversions on this chart happen DURING
 * signup, so the smaller units carry the interesting cases; a zero trailing
 * unit is dropped, so an exact gap reads "11d" and not "11d 0h".
 */
const elapsed = (days: number) => {
  // Never round down to "0m": every row here did convert.
  const total = Math.max(1, Math.round(days * 24 * 60));
  const d = Math.floor(total / 1440);
  const h = Math.floor((total % 1440) / 60);
  const m = total % 60;
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
};

/**
 * The accounts behind one bar of the cohort chart: its numerator, listed.
 * Opened by clicking a bar; the eligible-but-unconverted are deliberately not
 * shown, because the question a bar raises is "who is this percentage".
 *
 * Every paid plan start counts, trial or outright purchase, because the bar
 * counts both. The Plan column carries which it was.
 *
 * On a money bar the list is narrower still: only accounts actually paying,
 * with what each is worth. That is the bar's own population, so the two always
 * add up; the API is told which to list rather than guessing.
 */
const CohortMembersSheet = ({ slug, metricKey, selection, onClose }: Props) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState<CohortMember[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!selection) return;

    // A new selection starts from the loading state rather than showing the
    // previous bucket's accounts under the new bucket's heading.
    setMembers(null);
    setError(false);

    let live = true;
    changelogService
      .cohortMembers(slug, metricKey, selection.cohort, selection.fromDay, selection.toDay)
      .then((res) => live && setMembers(res.agencies))
      .catch(() => live && setError(true));

    return () => {
      live = false;
    };
  }, [slug, metricKey, selection]);

  return (
    <Sheet open={selection !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-xl gap-0 p-0">
        {selection && (
          <>
            <SheetHeader className="border-b px-5 py-4 gap-1">
              <SheetTitle className="text-base">
                {selection.cohortLabel} · {selection.bucketLabel}
              </SheetTitle>
              <SheetDescription className="text-xs">
                {selection.unit === 'currency' ? (
                  <>
                    {selection.amount !== undefined &&
                      `$${Math.round(selection.amount).toLocaleString()} MRR from `}
                    {selection.converted} paying{' '}
                    {selection.converted === 1 ? 'account' : 'accounts'}
                    {selection.value !== null &&
                      ` · $${selection.value.toFixed(2)} per account created`}
                  </>
                ) : (
                  <>
                    {selection.converted} of {selection.eligible} accounts
                    {selection.value !== null && ` · ${selection.value.toFixed(1)}%`}
                  </>
                )}
                {' · '}
                {selection.toDay === null
                  ? 'started a paid plan at any point since signing up.'
                  : `started a paid plan ${selection.fromDay} to ${selection.toDay} days after signing up.`}
              </SheetDescription>
            </SheetHeader>

            <ScrollArea className="flex-1 min-h-0" type="always">
              {error ? (
                <p className="px-5 py-14 text-center text-sm text-muted-foreground">
                  Couldn't load the accounts behind this bar
                </p>
              ) : members === null ? (
                <div className="space-y-2.5 px-5 py-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : members.length === 0 ? (
                <p className="px-5 py-14 text-center text-sm text-muted-foreground">
                  No accounts converted in this bucket
                </p>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-background">
                    <TableRow>
                      <TableHead>Agency</TableHead>
                      <TableHead>Signed up</TableHead>
                      <TableHead className="text-right">Time to convert</TableHead>
                      <TableHead>Plan</TableHead>
                      {selection.unit === 'currency' && (
                        <TableHead className="text-right">MRR</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((m) => (
                      <TableRow
                        key={m.agencyId}
                        className="cursor-pointer"
                        onClick={() => navigate(`/agencies/${m.agencyId}`)}
                      >
                        <TableCell className="font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            {m.name || 'Unnamed agency'}
                            <ExternalLink className="size-3 text-muted-foreground" />
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {dateTime(m.signedUpAt)}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          {elapsed(m.daysToConvert)}
                        </TableCell>
                        <TableCell>
                          {m.planName ?? <span className="text-muted-foreground">Unknown</span>}
                        </TableCell>
                        {selection.unit === 'currency' && (
                          <TableCell className="text-right font-mono tabular-nums">
                            ${m.mrr.toFixed(2)}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CohortMembersSheet;
